import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { MessageBubble } from '../components/conversation/MessageBubble';
import { ReplyBox } from '../components/conversation/ReplyBox';
import { PortalSpinner } from '../components/PortalSpinner';
import { getInitials, formatDateLabel } from '../../utils/helpers';
import { useConversationsQuery } from '../queries/conversations';
import { useMessagesQuery, useSendMessageMutation } from '../queries/messages';
import { Message } from '../services/patientApi';
import { PORTAL_ROUTES } from '../constants';
import { useConversationSocket } from '../hooks/useConversationSocket';
import { useMessageScroll } from '../hooks/useMessageScroll';

export const ConversationShow = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [message, setMessage] = useState('');

  const { data: conversationsData } = useConversationsQuery();
  const selected = conversationsData?.pages.flatMap((p) => p.items).find((c) => c.attributes.encoded_id === id);

  useConversationSocket(id ?? null);

  const { data: messagesData, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useMessagesQuery(id ?? null);
  const messages = [...(messagesData?.pages.flatMap((p) => p.items) ?? [])].reverse();

  const { mutate: sendMsg, isPending: sending } = useSendMessageMutation(id ?? null);

  const { bottomRef, setScrollRef, sentinelRef } = useMessageScroll({
    pagesLength: messagesData?.pages.length ?? 0,
    messagesLength: messages.length,
    isFetchingNextPage,
    hasNextPage: !!hasNextPage,
    fetchNextPage,
  });

  const thread = useMemo(() => {
    const groups: Record<string, Message[]> = {};
    for (const msg of messages) {
      const date = msg.attributes.time.slice(0, 10);
      if (!groups[date]) groups[date] = [];
      groups[date].push(msg);
    }
    return Object.entries(groups).map(([date, msgs]) => ({ date, messages: msgs }));
  }, [messages]);

  const handleSend = () => {
    if (!message.trim() || !id) return;
    sendMsg(message.trim(), { onSuccess: () => setMessage('') });
  };

  return (
    <>
      <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(PORTAL_ROUTES.CONVERSATIONS)}
            className="md:hidden w-8 h-8 flex items-center justify-center text-secondary-500 hover:bg-gray-100 rounded-lg transition-colors -ml-1"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="w-10 h-10 rounded-full bg-teamcare-blue text-white flex items-center justify-center text-sm font-bold">
            {getInitials(selected?.attributes?.title ?? '')}
          </div>
          <div>
            <span className="font-bold text-secondary-900">{selected?.attributes?.title}</span>
            {selected?.attributes?.patient_number && (
              <p className="text-xs text-secondary-400 mt-0.5">{selected.attributes.patient_number}</p>
            )}
          </div>
        </div>
      </div>

      <div ref={setScrollRef} className="flex-1 overflow-y-auto px-4 md:px-6 py-4">
        {(hasNextPage || isFetchingNextPage) && (
          <div ref={sentinelRef} className="flex justify-center py-3">
            <PortalSpinner />
          </div>
        )}
        {isLoading && (
          <div className="flex justify-center pt-10">
            <PortalSpinner />
          </div>
        )}
        {thread.map((group) => (
          <div key={group.date} className="mb-6">
            <div className="flex items-center justify-center mb-4">
              <span className="text-[11px] font-semibold text-secondary-400 uppercase tracking-wider">
                {formatDateLabel(group.date)}
              </span>
            </div>
            <div className="space-y-3">
              {group.messages.map((msg) => (
                <MessageBubble key={msg.id} msg={msg} />
              ))}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {selected?.attributes?.state === 'closed' ? (
        <div className="flex-shrink-0 border-t border-gray-100 px-6 py-4 flex items-center justify-center gap-2 text-sm text-secondary-500 bg-gray-50">
          <span className="w-5 h-5 rounded-full bg-teal-500 flex items-center justify-center flex-shrink-0">
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
              <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          This conversation has been resolved.
        </div>
      ) : (
        <ReplyBox
          patientFirstName={selected?.attributes?.title ?? ''}
          message={message}
          onMessageChange={setMessage}
          onSend={handleSend}
          isSending={sending}
        />
      )}
    </>
  );
};
